import React from 'react';
import { Modal, Button, Card, Row, Col, Statistic, Table, Alert } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';

interface AllVehiclesEfficiencyModalProps {
  visible: boolean;
  onClose: () => void;
  t: (key: string) => string;
  loading: boolean;
  allVehiclesData: any;
  isMobile: boolean;
  recalculating: boolean;
  onRecalculate: () => void;
}

const AllVehiclesEfficiencyModal: React.FC<AllVehiclesEfficiencyModalProps> = ({
  visible,
  onClose,
  t,
  loading,
  allVehiclesData,
  isMobile,
  recalculating,
  onRecalculate
}) => {
  const getAllVehiclesChartData = () => {
    if (!allVehiclesData || !allVehiclesData.vehicles) return [];

    const allDates = new Set<string>();
    allVehiclesData.vehicles.forEach((vehicle: any) => {
      vehicle.records.forEach((record: any) => {
        allDates.add(dayjs(record.date).format('YYYY-MM-DD'));
      });
    });

    const sortedDates = Array.from(allDates).sort();

    return sortedDates.map(date => {
      const dataPoint: any = { date };
      
      allVehiclesData.vehicles.forEach((vehicle: any) => {
        const record = vehicle.records.find((r: any) => 
          dayjs(r.date).format('YYYY-MM-DD') === date
        );
        if (record) {
          dataPoint[`vehicle_${vehicle.vehicleId}`] = parseFloat(record.litersPer100Km.toFixed(2));
        }
      });

      return dataPoint;
    });
  };

  const getLineColor = (index: number) => {
    const colors = [
      '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
      '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
    ];
    return colors[index % colors.length];
  };

  return (
    <Modal
      title={t('all.vehicles.fuel.efficiency')}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="recalculate" type="primary" icon={<ReloadOutlined />} loading={recalculating} onClick={onRecalculate}>
          {t('recalculate.last.30.days') || 'Recalculate Last 30 Days'}
        </Button>,
        <Button key="close" onClick={onClose}>
          {t('close')}
        </Button>
      ]}
      width={isMobile ? '95%' : 1200}
      style={{ top: isMobile ? 10 : undefined }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
        </div>
      ) : allVehiclesData && allVehiclesData.vehicles.length > 0 ? (
        <>
          {/* Overall Statistics */}
          <Card size="small" style={{ marginBottom: 24, backgroundColor: '#f0f2f5' }}>
            <h4 style={{ marginBottom: 12 }}>{t('overall.statistics')}</h4>
            <Row gutter={[12, 12]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('average.efficiency')}
                  value={allVehiclesData.overall.averageLitersPer100Km?.toFixed(2)}
                  suffix="L/100km"
                  valueStyle={{ fontSize: isMobile ? 14 : 24 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('best.efficiency')}
                  value={allVehiclesData.overall.bestLitersPer100Km?.toFixed(2)}
                  suffix="L/100km"
                  valueStyle={{ color: '#3f8600', fontSize: isMobile ? 14 : 24 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('worst.efficiency')}
                  value={allVehiclesData.overall.worstLitersPer100Km?.toFixed(2)}
                  suffix="L/100km"
                  valueStyle={{ color: '#cf1322', fontSize: isMobile ? 14 : 24 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('total.distance')}
                  value={allVehiclesData.overall.totalDistanceKm?.toFixed(0)}
                  suffix="km"
                  valueStyle={{ fontSize: isMobile ? 14 : 24 }}
                />
              </Col>
            </Row>
            <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
              <Col xs={8}>
                <Statistic
                  title={t('total.vehicles')}
                  value={allVehiclesData.overall.totalVehicles}
                  valueStyle={{ fontSize: isMobile ? 14 : 24 }}
                />
              </Col>
              <Col xs={8}>
                <Statistic
                  title={t('total.records')}
                  value={allVehiclesData.overall.totalRecords}
                  valueStyle={{ fontSize: isMobile ? 14 : 24 }}
                />
              </Col>
              <Col xs={8}>
                <Statistic
                  title={t('total.fuel')}
                  value={allVehiclesData.overall.totalFuelLiters?.toFixed(isMobile ? 0 : 2)}
                  suffix="L"
                  valueStyle={{ fontSize: isMobile ? 14 : 24 }}
                />
              </Col>
            </Row>
          </Card>

          {/* Efficiency Trend Chart - Last 30 Days */}
          <div style={{ marginBottom: 24 }}>
            <h4>{t('efficiency.trend')} ({t('last.30.days.records')})</h4>
            <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
              <LineChart data={getAllVehiclesChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: isMobile ? 9 : 12 }}
                  angle={isMobile ? -45 : 0}
                  textAnchor={isMobile ? 'end' : 'middle'}
                  height={isMobile ? 60 : 30}
                />
                <YAxis 
                  label={isMobile ? undefined : { value: t('efficiency.liters.per.100km'), angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: isMobile ? 9 : 12 }}
                />
                <Tooltip />
                <Legend 
                  wrapperStyle={{ fontSize: isMobile ? 9 : 12 }}
                  layout={isMobile ? 'horizontal' : 'horizontal'}
                />
                {allVehiclesData.vehicles.map((vehicle: any, index: number) => (
                  <Line
                    key={vehicle.vehicleId}
                    type="monotone"
                    dataKey={`vehicle_${vehicle.vehicleId}`}
                    name={vehicle.licensePlate}
                    stroke={getLineColor(index)}
                    strokeWidth={isMobile ? 1.5 : 2}
                    connectNulls={true}
                    dot={{ r: isMobile ? 2 : 3 }}
                    activeDot={{ r: isMobile ? 3 : 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Vehicles Summary Table */}
          <Table
            dataSource={allVehiclesData.vehicles}
            rowKey="vehicleId"
            pagination={{ pageSize: 5 }}
            size="small"
            scroll={{ x: isMobile ? 500 : undefined }}
            columns={[
              {
                title: t('vehicle'),
                dataIndex: 'licensePlate',
                key: 'licensePlate',
                fixed: isMobile ? 'left' : undefined,
                width: isMobile ? 90 : undefined,
              },
              {
                title: isMobile ? 'Avg' : t('average.efficiency'),
                dataIndex: 'averageLitersPer100Km',
                key: 'averageLitersPer100Km',
                width: isMobile ? 70 : undefined,
                render: (value) => value ? `${value.toFixed(2)}` : t('n.a'),
                sorter: (a: any, b: any) => (a.averageLitersPer100Km || 0) - (b.averageLitersPer100Km || 0),
              },
              {
                title: isMobile ? 'Best' : t('best.efficiency'),
                dataIndex: 'bestLitersPer100Km',
                key: 'bestLitersPer100Km',
                width: isMobile ? 70 : undefined,
                render: (value) => value ? `${value.toFixed(2)}` : t('n.a'),
              },
              {
                title: isMobile ? 'Worst' : t('worst.efficiency'),
                dataIndex: 'worstLitersPer100Km',
                key: 'worstLitersPer100Km',
                width: isMobile ? 70 : undefined,
                render: (value) => value ? `${value.toFixed(2)}` : t('n.a'),
              },
              {
                title: isMobile ? 'km' : t('total.distance'),
                dataIndex: 'totalDistanceKm',
                key: 'totalDistanceKm',
                width: isMobile ? 70 : undefined,
                render: (value) => `${value.toFixed(0)}`,
                sorter: (a: any, b: any) => a.totalDistanceKm - b.totalDistanceKm,
              },
              {
                title: isMobile ? '#' : t('total.records'),
                dataIndex: 'recordCount',
                key: 'recordCount',
                width: isMobile ? 50 : undefined,
                sorter: (a: any, b: any) => a.recordCount - b.recordCount,
              },
            ]}
          />
        </>
      ) : (
        <Alert
          message={t('no.efficiency.data')}
          description={t('no.data.available')}
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
};

export default AllVehiclesEfficiencyModal;
